import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiGet, apiPatch, apiPost, TENANT_TOKEN_KEY } from '../../api/client';
import { useAgentSelector } from '../../auth/AgentSelectorContext';
import { Button, Card, Input } from '../../design-system';
import { tokens } from '../../design-system/tokens';
import { showGrowl } from '../../ui/growlState';

type GoogleAdsState = {
  id: string | null;
  status: string;
  connectedAt: string | null;
  lastError: string | null;
  customerId: string;
  conversionActionId: string;
  exclusionUserListId: string;
  excludeUnqualified: boolean;
};

export function AdsConnectorsPage() {
  const { selectedAgentId, selectedAgent } = useAgentSelector();
  const [searchParams] = useSearchParams();
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem(TENANT_TOKEN_KEY) : null;
  const [oauthConfigured, setOauthConfigured] = useState(false);
  const [ads, setAds] = useState<GoogleAdsState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!token || !selectedAgentId) return;
    setLoading(true);
    try {
      const data = await apiGet<{ oauthConfigured: boolean; googleAds: GoogleAdsState }>(
        `/api/console/lead-connectors?agentId=${encodeURIComponent(selectedAgentId)}`,
        token,
      );
      setOauthConfigured(data.oauthConfigured);
      setAds(data.googleAds);
    } catch (e) {
      showGrowl((e as Error).message, 'error');
    } finally {
      setLoading(false);
    }
  }, [token, selectedAgentId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (searchParams.get('connected') === '1') {
      showGrowl('Google Ads conectado.', 'success');
      void load();
    }
    const err = searchParams.get('error');
    if (err) showGrowl(`No se pudo conectar Google Ads (${err}).`, 'error');
  }, [searchParams, load]);

  const connect = async () => {
    if (!token || !selectedAgentId) return;
    try {
      const data = await apiGet<{ url: string }>(
        `/api/console/lead-connectors/google-ads/oauth-url?agentId=${encodeURIComponent(selectedAgentId)}`,
        token,
      );
      window.location.href = data.url;
    } catch (e) {
      showGrowl((e as Error).message, 'error');
    }
  };

  const save = async () => {
    if (!token || !selectedAgentId || !ads) return;
    setSaving(true);
    try {
      await apiPatch(
        `/api/console/lead-connectors/google-ads?agentId=${encodeURIComponent(selectedAgentId)}`,
        {
          customerId: ads.customerId,
          conversionActionId: ads.conversionActionId,
          exclusionUserListId: ads.exclusionUserListId,
          excludeUnqualified: ads.excludeUnqualified,
        },
        token,
      );
      showGrowl('Configuración guardada.', 'success');
    } catch (e) {
      showGrowl((e as Error).message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const disconnect = async () => {
    if (!token || !selectedAgentId) return;
    try {
      await apiPost(
        `/api/console/lead-connectors/google-ads/disconnect?agentId=${encodeURIComponent(selectedAgentId)}`,
        {},
        token,
      );
      showGrowl('Google Ads desconectado.', 'success');
      void load();
    } catch (e) {
      showGrowl((e as Error).message, 'error');
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 720 }}>
      <h1 style={{ margin: '0 0 8px', fontSize: 22, color: tokens.colors.neutral[900] }}>Conectores de Ads</h1>
      <p style={{ margin: '0 0 20px', color: tokens.colors.neutral[600], fontSize: 14 }}>
        Agente: <strong>{selectedAgent?.name ?? '—'}</strong>. Los leads calificados de este agente se envían al destino
        conectado para retroalimentar campañas.
      </p>
      {loading ? (
        <p style={{ color: tokens.colors.neutral[500] }}>Cargando…</p>
      ) : (
        <Card variant="outlined" padding="lg">
          <h2 style={{ margin: '0 0 8px', fontSize: 16 }}>Google Ads</h2>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: tokens.colors.neutral[600] }}>
            Convertí leads calificados en la conversión “Qualified lead”. Los no calificados no se mandan como conversión;
            opcionalmente el teléfono hasheado va a una lista de exclusión.
          </p>
          <div style={{ marginBottom: 12, fontSize: 13 }}>
            Estado:{' '}
            <strong style={{ color: ads?.status === 'connected' ? tokens.colors.success.dark : tokens.colors.neutral[700] }}>
              {ads?.status === 'connected' ? 'Conectado' : 'Desconectado'}
            </strong>
          </div>
          {ads?.lastError ? (
            <p style={{ fontSize: 12, color: '#b91c1c' }}>Último error: {ads.lastError}</p>
          ) : null}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <Button
              type="button"
              onClick={() => void connect()}
              disabled={!oauthConfigured}
            >
              {ads?.status === 'connected' ? 'Reconectar Google Ads' : 'Conectar Google Ads'}
            </Button>
            {ads?.status === 'connected' ? (
              <Button type="button" variant="secondary" onClick={() => void disconnect()}>
                Desconectar
              </Button>
            ) : null}
          </div>
          {!oauthConfigured ? (
            <p style={{ fontSize: 12, color: tokens.colors.neutral[500] }}>
              Falta configurar GOOGLE_ADS_OAUTH_CLIENT_ID y GOOGLE_ADS_OAUTH_CLIENT_SECRET en el servidor.
            </p>
          ) : null}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 480 }}>
            <label style={{ fontSize: 12, fontWeight: 600 }}>
              ID de cliente de Google Ads
              <Input
                value={ads?.customerId ?? ''}
                onChange={(e) => setAds((s) => (s ? { ...s, customerId: e.target.value } : s))}
                placeholder="123-456-7890"
              />
            </label>
            <label style={{ fontSize: 12, fontWeight: 600 }}>
              ID de conversión Qualified lead
              <Input
                value={ads?.conversionActionId ?? ''}
                onChange={(e) => setAds((s) => (s ? { ...s, conversionActionId: e.target.value } : s))}
                placeholder="customers/…/conversionActions/…"
              />
            </label>
            <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500 }}>
              <input
                type="checkbox"
                checked={ads?.excludeUnqualified ?? false}
                onChange={(e) => setAds((s) => (s ? { ...s, excludeUnqualified: e.target.checked } : s))}
              />
              Excluir no calificados (Customer Match)
            </label>
            {ads?.excludeUnqualified ? (
              <label style={{ fontSize: 12, fontWeight: 600 }}>
                ID de lista de exclusión
                <Input
                  value={ads.exclusionUserListId}
                  onChange={(e) => setAds((s) => (s ? { ...s, exclusionUserListId: e.target.value } : s))}
                />
              </label>
            ) : null}
            <Button type="button" onClick={() => void save()} disabled={saving}>
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
